<script setup lang="ts">
import IconLucideMic from '~icons/lucide/mic';
import IconLucideMicOff from '~icons/lucide/mic-off';
import IconLucideVideo from '~icons/lucide/video';
import IconLucideVideoOff from '~icons/lucide/video-off';
import IconLucideVolume2 from '~icons/lucide/volume-2';
import IconLucideVolumeX from '~icons/lucide/volume-x';
import { ref } from 'vue';
import type { MediaDeviceOption } from '../jitsi/runtime';
import CIconButton from './primitives/CIconButton.vue';
import CMenu from './primitives/CMenu.vue';
import CMenuItem from './primitives/CMenuItem.vue';

const props = defineProps<{
  audioEnabled: boolean;
  audioInputOptions: MediaDeviceOption[];
  audioOutputOptions: MediaDeviceOption[];
  audioUnavailable?: boolean;
  cameraEnabled: boolean;
  cameraUnavailable?: boolean;
  displayName: string;
  selectedAudioInput: string;
  selectedAudioOutput: string;
}>();

const emit = defineEmits<{
  (event: 'toggle-audio'): void;
  (event: 'toggle-camera'): void;
  (event: 'update:selected-audio-input', value: string): void;
  (event: 'update:selected-audio-output', value: string): void;
}>();

function buttonTone(unavailable: boolean | undefined): 'muted' | 'neutral' {
  if (unavailable) {
    return 'muted';
  }

  return 'neutral';
}

const inputLevel = ref(1);
const outputLevel = ref(1);
const showAudioInputMenu = ref(false);
const showAudioOutputMenu = ref(false);

function selectAudioInput(value: string, close: () => void): void {
  emit('update:selected-audio-input', value);
  close();
}

function selectAudioOutput(value: string, close: () => void): void {
  emit('update:selected-audio-output', value);
  close();
}
</script>

<template>
  <section class="border-conference-line-strong bg-conference-shell border-b px-2 py-1.5">
    <div class="flex items-center justify-between gap-3">
      <p class="min-w-0 truncate text-[12px] font-semibold text-white">{{ displayName }}</p>

      <div class="flex items-center gap-1">
        <CIconButton
          class="h-8 w-8"
          size="control"
          data-testid="track-toggle-audio"
          :tone="buttonTone(audioUnavailable)"
          :aria-label="audioEnabled ? 'Mute microphone' : 'Enable microphone'"
          :aria-pressed="audioUnavailable ? undefined : audioEnabled"
          :disabled="audioUnavailable"
          @click="emit('toggle-audio')"
        >
          <IconLucideMic v-if="audioEnabled" class="size-4" />
          <IconLucideMicOff v-else class="size-4" />
        </CIconButton>

        <CIconButton
          class="h-8 w-8"
          size="control"
          data-testid="track-toggle-camera"
          :tone="buttonTone(cameraUnavailable)"
          :aria-label="cameraEnabled ? 'Turn camera off' : 'Turn camera on'"
          :aria-pressed="cameraUnavailable ? undefined : cameraEnabled"
          :disabled="cameraUnavailable"
          @click="emit('toggle-camera')"
        >
          <IconLucideVideo v-if="cameraEnabled" class="size-4" />
          <IconLucideVideoOff v-else class="size-4" />
        </CIconButton>
      </div>
    </div>

    <div class="mt-1 grid grid-cols-2 gap-2 border-t border-white/10 pt-1.5">
      <div class="text-conference-shell-foreground flex min-w-0 items-center gap-1.5">
        <CMenu v-model:open="showAudioInputMenu" align="left" tone="light" width="md" panel-test-id="audio-input-menu">
          <template #trigger="{ open, toggle }">
            <button
              type="button"
              class="text-conference-shell-foreground hover:text-conference-accent inline-flex size-4 shrink-0 items-center justify-center transition"
              :aria-expanded="open"
              aria-haspopup="menu"
              aria-label="Audio input menu"
              @click="toggle"
            >
              <IconLucideMic class="size-3.5" />
            </button>
          </template>

          <template #default="{ close }">
            <div class="min-w-48 py-1">
              <p
                class="border-conference-line-soft border-b px-2.5 py-1 text-[10px] font-semibold tracking-[0.18em] text-slate-500 uppercase"
              >
                {{ props.audioInputOptions.length > 0 ? 'Input' : 'No Device Found' }}
              </p>
              <template v-if="props.audioInputOptions.length > 0">
                <CMenuItem
                  v-for="option in props.audioInputOptions"
                  :key="option.value"
                  tone="light"
                  @click="selectAudioInput(option.value, close)"
                >
                  <template #trailing>
                    <span v-if="option.value === props.selectedAudioInput" class="text-conference-accent-muted">●</span>
                  </template>
                  {{ option.label }}
                </CMenuItem>
              </template>
            </div>
          </template>
        </CMenu>

        <input
          v-model="inputLevel"
          class="accent-conference-accent-deep min-w-0 flex-1"
          max="2"
          min="0"
          step="0.01"
          type="range"
        />
        <IconLucideVolume2 class="text-conference-accent size-3.5 shrink-0" />
      </div>

      <div class="text-conference-shell-foreground flex min-w-0 items-center gap-1.5">
        <CMenu v-model:open="showAudioOutputMenu" align="left" tone="light" width="md" panel-test-id="audio-output-menu">
          <template #trigger="{ open, toggle }">
            <button
              type="button"
              class="text-conference-shell-foreground hover:text-conference-accent inline-flex size-4 shrink-0 items-center justify-center transition"
              :aria-expanded="open"
              aria-haspopup="menu"
              aria-label="Audio output menu"
              @click="toggle"
            >
              <IconLucideVolumeX class="size-3.5" />
            </button>
          </template>

          <template #default="{ close }">
            <div class="min-w-48 py-1">
              <p
                class="border-conference-line-soft border-b px-2.5 py-1 text-[10px] font-semibold tracking-[0.18em] text-slate-500 uppercase"
              >
                {{ props.audioOutputOptions.length > 0 ? 'Output' : 'No Device Found' }}
              </p>
              <template v-if="props.audioOutputOptions.length > 0">
                <CMenuItem
                  v-for="option in props.audioOutputOptions"
                  :key="option.value"
                  tone="light"
                  @click="selectAudioOutput(option.value, close)"
                >
                  <template #trailing>
                    <span v-if="option.value === props.selectedAudioOutput" class="text-conference-accent-muted">●</span>
                  </template>
                  {{ option.label }}
                </CMenuItem>
              </template>
            </div>
          </template>
        </CMenu>

        <input
          v-model="outputLevel"
          class="accent-conference-accent-deep min-w-0 flex-1"
          max="1"
          min="0"
          step="0.01"
          type="range"
        />
        <IconLucideVolume2 class="text-conference-accent size-3.5 shrink-0" />
      </div>
    </div>

    <div class="sr-only">
      {{ props.selectedAudioInput }}
      {{ props.selectedAudioOutput }}
    </div>
  </section>
</template>
